import React, { useEffect, useState } from 'react';
import { WithTranslationsProps } from 'react-utilities';
import { regex } from 'core-utilities';
import { Loading, Link } from 'react-style-guide';
import { TGetGameDetails } from '../../common/types/bedev1Types';
import bedev1Services from '../../common/services/bedev1Services';
import {
  getAbbreviatedValue,
  getNumberFormat,
  dateTimeFormatter
} from '../../common/utils/parsingUtils';
import { buildReportAbuseUrl } from '../../common/utils/browserUtils';
import { FeatureGameDetails } from '../../common/constants/translationConstants';
import GearOptionsDisplay from './GearOptionsDisplay';
import '../../../../css/gameDetails/_description.scss';

export type TDescriptionProps = {
  universeId: string;
  placeId: string;
  translate: WithTranslationsProps['translate'];
};

export type TDescriptionLabel = {
  label: string;
  value: React.ReactNode;
};

export const Description = ({ placeId, universeId, translate }: TDescriptionProps): JSX.Element => {
  const [gameDetails, setGameDetails] = useState<TGetGameDetails | undefined>(undefined);

  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        const response = await bedev1Services.getGameDetails(universeId);
        setGameDetails(response);
      } catch (e) {
        console.error(e);
      }
    };

    // eslint-disable-next-line no-void
    void fetchGameDetails();
  }, []);

  if (gameDetails === undefined) {
    return <Loading />;
  }
  return (
    <div className='game-description-container'>
      <h3>{translate(FeatureGameDetails.HeadingDescription)}</h3>
      <pre className='text game-description'>
        {gameDetails.description?.split(regex.url).map((text, index) => {
          if (text.search(regex.url) > -1) {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <a key={index} data-testid='game-description-link' className='text-link' href={text}>
                {text}
              </a>
            );
          }
          return text;
        })}
      </pre>
      <ul className='border-top border-bottom game-stat-container'>
        {([
          {
            label: translate(FeatureGameDetails.LabelPlaying),
            value: getNumberFormat(gameDetails.playing)
          },
          {
            label: translate(FeatureGameDetails.LabelFavorites),
            value: getNumberFormat(gameDetails.favoritedCount)
          },
          {
            label: translate(FeatureGameDetails.LabelVisits),
            value: getAbbreviatedValue(gameDetails.visits)
          },
          {
            label: translate(FeatureGameDetails.LabelCreated),
            value: dateTimeFormatter.getShortDate(gameDetails.created)
          },
          {
            label: translate(FeatureGameDetails.LabelUpdated),
            value: dateTimeFormatter.getShortDate(gameDetails.updated)
          },
          {
            label: translate(FeatureGameDetails.LabelMaxPlayers),
            value: getNumberFormat(gameDetails.maxPlayers)
          },
          {
            label: translate(FeatureGameDetails.LabelGenre),
            value: gameDetails.genre
          },
          {
            label: translate(FeatureGameDetails.LabelAllowedGear),
            value: (
              <GearOptionsDisplay
                genre={gameDetails.genre}
                isGenreEnforced={gameDetails.isGenreEnforced}
                isAllGenre={gameDetails.isAllGenre}
                allowedGearCategories={gameDetails.allowedGearCategories}
              />
            )
          }
        ] as TDescriptionLabel[]).map(stat => (
          <li key={stat.label} className='game-stat'>
            <p className='text-label text-overflow font-caption-header'>{stat.label}</p>
            <p className='text-lead font-caption-body'>{stat.value}</p>
          </li>
        ))}
      </ul>
      <div className='game-description-footer'>
        {gameDetails.copyingAllowed && (
          <p className='text-pastname'>{translate(FeatureGameDetails.LabelPlaceCopyingAllowed)}</p>
        )}
        <Link className='text-report' url={buildReportAbuseUrl(placeId, gameDetails.name)}>
          {translate(FeatureGameDetails.LabelReportAbuse)}
        </Link>
      </div>
    </div>
  );
};

export default Description;
